// ANGEL FLIGHT — economy: combo multiplier + payout math (pure).
//
// SCRIP is the soft per-kill currency, scaled by a kill-streak combo multiplier
// that rises on consecutive kills and decays after a quiet window. SALVAGE is the
// hard currency (elites, objectives, bosses). All pure + unit-tested so the
// "feels rewarding" loop is verifiable without booting Phaser.

import { BALANCE } from './balance';
import type { DifficultyDef } from '../core/types';

/** Live combo state. Mutated through the helpers below, never directly. */
export interface ComboState {
  /** Consecutive kills since the last decay. */
  count: number;
  /** ms since the last kill (drives decay). */
  sinceKillMs: number;
}

export function newCombo(): ComboState {
  return { count: 0, sinceKillMs: 0 };
}

/** The multiplier tier index for a given kill count. */
function stepIndex(count: number): number {
  const idx = Math.floor(count / BALANCE.combo.killsPerStep);
  const max = BALANCE.combo.steps.length - 1;
  return idx > max ? max : idx;
}

/** The current combo multiplier (≥1) for a combo state. */
export function comboMult(c: ComboState): number {
  return BALANCE.combo.steps[stepIndex(c.count)] ?? 1;
}

/** Register a kill: bump the count, reset the decay clock. Returns new mult. */
export function registerKill(c: ComboState): number {
  c.count += 1;
  c.sinceKillMs = 0;
  return comboMult(c);
}

/**
 * Advance the decay clock by `dt` ms. If the quiet window elapses, the combo
 * resets to 0. Returns true if the combo just reset this tick.
 */
export function tickCombo(c: ComboState, dt: number): boolean {
  if (c.count === 0) return false;
  c.sinceKillMs += dt;
  if (c.sinceKillMs >= BALANCE.combo.decayMs) {
    c.count = 0;
    c.sinceKillMs = 0;
    return true;
  }
  return false;
}

/** A miss/hit-taken can break the combo immediately (call from damage system). */
export function breakCombo(c: ComboState): void {
  c.count = 0;
  c.sinceKillMs = 0;
}

/**
 * SCRIP awarded for a kill: base bounty × combo multiplier × difficulty payout,
 * floored to an integer.
 */
export function scripForKill(
  bounty: number,
  combo: ComboState,
  diff: DifficultyDef,
): number {
  return Math.floor(bounty * comboMult(combo) * diff.payoutMult);
}

/** A headshot kill pays this multiple of the round's SCRIP on top of the combo —
 *  the precision reward. The SINGLE source for the bonus (EconomySystem applies
 *  it via scripForKillZoned, never an inline magic number). */
export const HEADSHOT_SCRIP_BONUS = 1.5;

/**
 * SCRIP for a kill including the headshot precision bonus when the killing zone
 * was the head (bonus applied AFTER the combo/difficulty payout, then rounded).
 * Keeps the headshot reward in the economy module, not scattered in systems.
 */
export function scripForKillZoned(
  bounty: number,
  combo: ComboState,
  diff: DifficultyDef,
  headshot: boolean,
): number {
  const base = scripForKill(bounty, combo, diff);
  return headshot ? Math.round(base * HEADSHOT_SCRIP_BONUS) : base;
}

/** SALVAGE awarded for a milestone (elite kill, objective). Difficulty-scaled. */
export function salvageReward(base: number, diff: DifficultyDef): number {
  return Math.max(0, Math.round(base * diff.payoutMult));
}

// NOTE: the mission SCORE formula lives in config/grading.ts (scoreMission) — it
// is the single source feeding both the GameLoop best-score board and the
// Debrief grade. Keep score math there, not here.
